// Postnatal analogue of buildInitialObsState (KIN-1075): a per-baby form-state
// map keyed by foetusIdentifier, seeded from any existing baby observations
// (matched by foetusId). Values are strings for the text inputs; feeding is
// derived from the breast/formula booleans.
const today = new Date().toISOString().split("T")[0];

const toFeeding = (observation) => {
  if (observation?.breastFeeding && observation?.formulaFeeding) {
    return "Breast and Formula combination";
  }
  if (observation?.formulaFeeding) return "Formula";

  return "Breast";
};

const toStringOrEmpty = (value) => (value != null ? String(value) : "");

export const buildInitialBabyObsState = (foetuses, existingObservations) => {
  const map = {};

  foetuses.forEach((foetus) => {
    const observation = existingObservations?.find(
      (item) =>
        item.foetusId === foetus.id ||
        item.pregnancyFoetus?.foetusIdentifier === foetus.foetusIdentifier
    );

    map[foetus.foetusIdentifier] = {
      foetusId: foetus.id,
      observationId: observation?.id ?? null,
      bornAt: observation?.bornAt ?? today,
      deliveryMethod: observation?.deliveryMethod ?? "c-section",
      weightAtBirth: toStringOrEmpty(observation?.weightAtBirthGrams),
      feeding: observation ? toFeeding(observation) : "Breast",
      phnVisit: observation?.phnVisit ?? "",
      issuesOfNote: observation?.issuesOfNote ?? "",
      jaundice: observation?.jaundice === "Yes",
      weight: toStringOrEmpty(observation?.weightInG),
      length: toStringOrEmpty(observation?.lengthInCm),
      headCircumference: toStringOrEmpty(observation?.headCircumferenceInCm),
      crt: observation?.crt ?? "no",
      umbilicus: observation?.umbilicus ?? "",
      barlowAndOrtolaniTest: observation?.barlowAndOrtolaniTest ?? "",
      heart: observation?.heart ?? "",
      fontanelles: observation?.fontanelles ?? "",
      buccalMucosa: observation?.buccalMucosa ?? "",
      feet: observation?.feet ?? "",
      impression: observation?.impression ?? "",
      plan: observation?.plan ?? "",
    };
  });

  return map;
};

// Required per-baby fields (mirrors the old flat postnatal schema). Returns an
// errors map keyed by foetusIdentifier → { field: message }; empty when valid.
const REQUIRED_BABY_FIELDS = {
  bornAt: "Enter the information about Born Date.",
  weightAtBirth: "Enter the information about Weight At Birth.",
  phnVisit: "Enter the information about PHN Visit.",
  issuesOfNote: "Enter the information about issues of note.",
  weight: "Enter the information about Weight.",
  length: "Enter the information about Length.",
  headCircumference: "Enter the information about Head Circumference.",
  umbilicus: "Enter the information about Umbilicus.",
  barlowAndOrtolaniTest: "Enter the information about Barlow and Ortolani's Test.",
  heart: "Enter the information about Heart.",
  fontanelles: "Enter the information about Fontanelles.",
  buccalMucosa: "Enter the information about Buccal Mucosa.",
  feet: "Enter the information about Feet.",
  impression: "Enter the information about Impression.",
  plan: "Enter the information about Plans.",
};

export const validateBabyObservations = (babyObservationsMap) => {
  const errors = {};

  Object.entries(babyObservationsMap ?? {}).forEach(([identifier, baby]) => {
    const babyErrors = {};

    Object.entries(REQUIRED_BABY_FIELDS).forEach(([field, message]) => {
      const value = baby?.[field];

      if (typeof value !== "string" || value.trim() === "") {
        babyErrors[field] = message;
      }
    });

    if (Object.keys(babyErrors).length > 0) errors[identifier] = babyErrors;
  });

  return errors;
};

// Map one baby's form state to the PostNatalConsultationBabyObservationDTO the
// BE expects (KIN-1075), given the parent consultation id.
export const toBabyObservationPayload = (babyState, postNatalConsultationId) => ({
  id: babyState.observationId ?? undefined,
  postNatalConsultationId,
  foetusId: babyState.foetusId,
  bornAt: babyState.bornAt,
  deliveryMethod: babyState.deliveryMethod,
  weightAtBirthGrams: babyState.weightAtBirth
    ? Number(babyState.weightAtBirth)
    : null,
  jaundice: babyState.jaundice ? "Yes" : "No",
  breastFeeding:
    babyState.feeding === "Breast" ||
    babyState.feeding === "Breast and Formula combination",
  formulaFeeding:
    babyState.feeding === "Formula" ||
    babyState.feeding === "Breast and Formula combination",
  issuesOfNote: babyState.issuesOfNote,
  phnVisit: babyState.phnVisit,
  weightInG: babyState.weight ? Number(babyState.weight) : null,
  lengthInCm: babyState.length ? Number(babyState.length) : null,
  headCircumferenceInCm: babyState.headCircumference
    ? Number(babyState.headCircumference)
    : null,
  crt: babyState.crt,
  umbilicus: babyState.umbilicus,
  barlowAndOrtolaniTest: babyState.barlowAndOrtolaniTest,
  heart: babyState.heart,
  fontanelles: babyState.fontanelles,
  buccalMucosa: babyState.buccalMucosa,
  feet: babyState.feet,
  impression: babyState.impression,
  plan: babyState.plan,
  isActive: true,
});
